import { ChevronDown, Phone } from "lucide-react";
import React, { forwardRef } from "react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const PhoneInput = forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
	({ className, ...props }, ref) => {
		return (
			<Input
				className={cn("-ms-px rounded-s-none h-14 shadow-none focus-visible:z-10", className)}
				ref={ref}
				{...props}
			/>
		);
	}
);
const PhoneNumberInput = forwardRef<HTMLInputElement>((props, ref) => {
	return (
		<div className="space-y-2" dir="ltr">
			<Label className="sr-only">Phone number input</Label>
			<RPNInput.default
				className="flex rounded-lg shadow-sm shadow-black/5 h-14"
				international
				flagComponent={FlagComponent}
				countrySelectComponent={CountrySelect}
				inputComponent={PhoneInput}
				placeholder="Enter phone number"
				// @ts-expect-error - `ref` is not a valid prop
				ref={ref}
				{...props}
			/>
		</div>
	);
});

PhoneNumberInput.displayName = "PhoneNumberInput";
export default PhoneNumberInput;

PhoneInput.displayName = "PhoneInput";

interface CountrySelectProps {
	disabled?: boolean;
	value: RPNInput.Country;
	onChange: (value: RPNInput.Country) => void;
	options: { label: string; value: RPNInput.Country | undefined }[];
}

function CountrySelect({ disabled, value, onChange, options }: CountrySelectProps) {
	const handleSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
		onChange(event.target.value as RPNInput.Country);
	};

	return (
		<div className="relative inline-flex items-center self-stretch rounded-s-lg border border-input bg-background py-2 pe-2 ps-3 text-muted-foreground transition-shadow focus-within:z-10 focus-within:border-ring focus-within:outline-none focus-within:ring-[3px] focus-within:ring-ring/20 hover:bg-accent hover:text-foreground has-[:disabled]:pointer-events-none has-[:disabled]:opacity-50">
			<div className="inline-flex items-center gap-1" aria-hidden="true">
				<FlagComponent country={value} countryName={value} aria-hidden="true" />
				<span className="text-muted-foreground/80">
					<ChevronDown size={16} strokeWidth={2} aria-hidden="true" />
				</span>
			</div>
			<select
				disabled={disabled}
				value={value}
				onChange={handleSelect}
				className="absolute inset-0 text-sm opacity-0"
				aria-label="Select country"
			>
				<option key="default" value="">
					Select a country
				</option>
				{options
					.filter(x => x.value)
					.map((option, i) => (
						<option key={option.value ?? `empty-${i}`} value={option.value}>
							{option.label}
							{" "}
							{option.value && `+${RPNInput.getCountryCallingCode(option.value)}`}
						</option>
					))}
			</select>
		</div>
	);
}

function FlagComponent({ country, countryName }: RPNInput.FlagProps) {
	const Flag = flags[country];

	return (
		<span className="w-5 overflow-hidden rounded-sm">
			{Flag ? <Flag title={countryName} /> : <Phone size={16} aria-hidden="true" />}
		</span>
	);
}
